<script>
  import { peddlrWalletKycLiteMethods, peddlrWalletKycLiteComputed, customerListLiteComputed, customerLiteMethods } from "@/state/helpers";
  import moment from "moment-timezone";
  import Swal from "sweetalert2";

  // Value used by getUserWalletsList to identify the eload (Digital Product) wallet + its enabled state.
  // Mirrors the constants used in profileWallet.vue so the freeze check stays consistent.
  const ELOAD_WALLET_TYPE = "eload wallet";
  const WALLET_STATUS_ENABLED = "Enabled";

  // KYC "age" (in months) at which an existing, verified KYC is considered stale enough to offer a
  // fresh KYC link. Configurable via VUE_APP_KYC_REVERIFY_MONTHS; defaults to 12 (1 year) when unset
  // or invalid so behaviour is predictable across environments.
  const KYC_REVERIFY_MONTHS = (() => {
    const parsed = parseInt(process.env.VUE_APP_KYC_REVERIFY_MONTHS, 10);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : 12;
  })();

  /**
   * Generate KYC (KYC Link Generator) card.
   *
   * Standalone card in the Peddlr Lite merchant-details left column. The whole card is gated on the
   * eload (Digital Product) wallet being FROZEN. Within that, its state resolves as:
   *   - "Generate KYC" button  = eload frozen AND (no KYC OR KYC age met)
   *   - Copy link (generated)  = eload frozen AND kyc_eload === 1 AND link exists AND not expired
   *   - "Re-generate" button   = eload frozen AND the KYC link has expired
   */
  export default {
    page: {
      title: "Generate KYC"
    },
    computed: {
      ...peddlrWalletKycLiteComputed,
      ...customerListLiteComputed,
      // A merchant with no KYC record at all.
      hasNoKyc() {
        const type = this.userKycStatus?.kycAcctType;
        return this.isEmpty(type) || type === 'NoKYC' || type === "Don't Have KYC";
      },
      // A merchant that already has KYC on file (verified/existing data we would be replacing).
      hasExistingKyc() {
        return !this.hasNoKyc;
      },
      // Merchant sits at manual verification in the DB (backend: kyc_acct_type = 'ManualKYC',
      // kyc_acct_status = 'ManualVerification').
      isManualVerificationStatus() {
        const type = this.userKycStatus?.kycAcctType;
        const status = this.userKycStatus?.kycAcctStatus;
        return type === 'ManualKYC' || status === 'ManualVerification';
      },
      // The DB status can lag behind Onfido, so for a ManualVerification merchant the backend re-checks the
      // live Onfido workflow and returns it as kycInfo.onfidoStatus (user-kyc-details). 'approved' means the
      // KYC is actually finished even though the DB still reads ManualVerification.
      isOnfidoApproved() {
        return this.kycInfo.onfidoStatus === 'approved';
      },
      // Onfido has approved the merchant (KYC complete), even if the DB status still lags at
      // ManualVerification. When this is true the card must stay hidden no matter what else is going on
      // (e.g. a leftover generated link), so it overrides every "reason to show" below.
      isKycComplete() {
        return this.isManualVerificationStatus && this.isOnfidoApproved;
      },
      // Manual verification that is NOT yet approved by Onfido = KYC still incomplete, so we keep offering
      // the generate / re-generate link. When Onfido has already approved them, KYC is done and this is
      // false (the card hides).
      isManualVerification() {
        return this.isManualVerificationStatus && !this.isOnfidoApproved;
      },
      // Verified KYC that is older than the configured re-verify threshold.
      isKycStale() {
        const verifiedAt = this.kycInfo.kycDateVerified;
        if (this.isEmpty(verifiedAt)) return false;
        const verified = moment(verifiedAt);
        if (!verified.isValid()) return false;
        return moment().diff(verified, 'months', true) >= KYC_REVERIFY_MONTHS;
      },
      // Truthy for the backend kyc_eload flag regardless of how it's serialized (true / 1 / "1").
      isEloadKyc() {
        const v = this.kycInfo.kycEload;
        return v === true || v === 1 || v === '1';
      },
      // Has an eload-generated (kyc_eload) link that has since expired.
      isLinkExpired() {
        return this.isEloadKyc
          && !this.isEmpty(this.generatedKycLink)
          && this.kycInfo.isKycUrlExpired === true;
      },
      // Hard prerequisite: the card only appears when the eload wallet is FROZEN.
      isEloadFrozen() {
        return this.eloadFrozen === true;
      },
      // The card is shown only when the eload wallet is frozen AND there is a KYC-related reason to act:
      // no KYC, KYC age met, an active generated link, an expired link to re-generate, OR the merchant is
      // at manual verification but NOT yet approved by Onfido (KYC unfinished, so a link is still useful).
      // If Onfido has already approved the merchant (isKycComplete) the card is ALWAYS hidden, even when a
      // generated link is still on file — otherwise the leftover link would keep the card visible.
      isEligible() {
        if (this.isKycComplete) return false;
        return this.isEloadFrozen
          && (this.hasNoKyc || this.isKycStale || this.hasActiveLink || this.isLinkExpired || this.isManualVerification);
      },
      // Show the copyable link only when it was generated from THIS ERP eload/Peddlr Lite flow
      // (backend kyc_eload tag) AND the link exists and has not expired.
      hasActiveLink() {
        return this.isEloadKyc
          && !this.isEmpty(this.generatedKycLink)
          && !this.kycInfo.isKycUrlExpired;
      },
      // "Generate New KYC Link" when there is an expired eload link; otherwise "Generate KYC".
      generateButtonLabel() {
        return this.isLinkExpired ? 'Generate New KYC Link' : 'Generate KYC';
      },
      // A generation that REPLACES existing data (existing KYC or an expired link) is a "re-KYC"; a
      // first-time generation (no KYC, no link) is a plain "KYC". Drives the confirm/success modal copy.
      isRegeneration() {
        return this.hasExistingKyc || this.isLinkExpired;
      },
      // --- Confirm modal copy (Figma: node 20212 for re-KYC) ---
      confirmModalTitle() {
        return this.isRegeneration ? 'Generate Re-KYC' : 'Generate KYC';
      },
      confirmModalBody() {
        return this.isRegeneration
          ? 'You are about to generate a re-KYC link. Please secure their consent before confirming.'
          : 'You are about to generate a KYC link. Please secure their consent before confirming.';
      },
      // --- Success modal copy (Figma: node 21622 for re-KYC) ---
      successModalTitle() {
        return this.isRegeneration ? 'Generated Re-KYC Successful!' : 'Generated KYC Successful!';
      },
      successModalBody() {
        return this.isRegeneration
          ? 'Please copy the generated link and send to the merchant so they can undergo re-KYC.'
          : 'Please copy the generated link and send to the merchant so they can undergo KYC.';
      },
      subscriberId() {
        // Prefer the id carried on the KYC record; fall back to the business record.
        return this.kycInfo.subscriberId || this.businessDetails?.subscriberId || null;
      }
    },
    data() {
      return {
        title: "Generate KYC",
        businessId: 0,
        // Local snapshot of the KYC detail record (kycUrl, isKycUrlExpired, kycDateVerified, subscriberId...).
        kycInfo: {
          subscriberId: null,
          kycUrl: null,
          isKycUrlExpired: false,
          kycDateVerified: null,
          kycUrlDate: null,
          // Backend-computed link expiry (generated_kyc_link_at + manual_kyc_callback.ttl), UTC ISO string.
          kycUrlExpiresAt: null,
          // Backend tag (ref_users.kyc_eload): true when the link was generated from this ERP flow.
          kycEload: false,
          // Live Onfido workflow status, re-checked by the backend only when the DB reads ManualVerification
          // (the stored status can lag behind Onfido). 'approved' => KYC actually complete.
          onfidoStatus: null,
        },
        generatedKycLink: '',
        isGenerating: false,
        showGenerateKycModal: false,
        showGenerateKycSuccessModal: false,
        // Eload (Digital Product) wallet freeze state; the whole card is gated on this being true.
        eloadFrozen: false,
      };
    },
    methods: {
      ...peddlrWalletKycLiteMethods,
      ...customerLiteMethods,
      isEmpty(value) {
        return (
          (typeof value == "undefined") ||
          (value == undefined) ||
          (value == null) ||
          (value === "") ||
          (Object.prototype.hasOwnProperty.call(value, 'length') && value.length === 0) ||
          (value.constructor === Object && Object.keys(value).length === 0)
        );
      },
      async fetchKycDetails() {
        if (this.isEmpty(this.businessId)) return;
        await this.getUserKycDetails({ businessId: this.businessId })
          .then(() => {
            this.kycInfo = this.userKycDetails || {};
            this.generatedKycLink = this.kycInfo.kycUrl || '';
          })
          .catch((err) => {
            console.error("fetchKycDetails error:", err);
          });
      },
      // Resolve the eload (Digital Product) wallet freeze state from getUserWalletsList. The list returns
      // { walletType, walletStatus } per wallet; the eload wallet is frozen when its status is not 'Enabled'.
      // Mirrors the mapping used in profileWallet.vue so both views agree.
      async fetchEloadWalletStatus() {
        if (this.isEmpty(this.businessId) || typeof this.getUserWalletsList !== 'function') return;
        await this.getUserWalletsList({ businessId: this.businessId })
          .then((res) => {
            const wallets = res || [];
            const eload = wallets.find((w) => w.walletType === ELOAD_WALLET_TYPE);
            // Frozen when an eload wallet exists and its status is not "Enabled".
            this.eloadFrozen = !!eload && eload.walletStatus !== WALLET_STATUS_ENABLED;
          })
          .catch((err) => {
            console.error("fetchEloadWalletStatus error:", err);
            this.eloadFrozen = false;
          });
      },
      // Figma fig3 note, e.g. "Link expires on May 25, 2026 at 06:15 AM. You can generate a new link
      // after it expires." Based on the backend-computed kycUrlExpiresAt (generated_kyc_link_at +
      // manual_kyc_callback.ttl) — the SAME source the old KYC Details page uses for expiry.
      linkExpiryNote() {
        const expiresAt = this.kycInfo.kycUrlExpiresAt;
        if (this.isEmpty(expiresAt)) return '';
        // kycUrlExpiresAt is a UTC ISO string; display it in PH time to match the app's date convention
        // (see the customFormatDate filter) and the Figma.
        const expires = moment.utc(expiresAt).tz('Asia/Manila');
        if (!expires.isValid()) return '';
        return `Link expires on ${expires.format('MMMM DD, YYYY')} at ${expires.format('hh:mm A')}. You can generate a new link after it expires.`;
      },
      // Entry point for the primary button. The confirmation modal warns that EXISTING data will be
      // replaced, so it shows whenever there is data to replace: the merchant already has KYC, OR this
      // is a "Re-generate" of an expired link. A first-time merchant (no KYC, no link) has nothing to
      // lose, so we skip the popup and generate directly.
      onGenerateClicked() {
        if (!this.isEligible) return;
        if (this.hasExistingKyc || this.isLinkExpired) {
          this.showGenerateKycModal = true;
        } else {
          this.generateKycLink();
        }
      },
      async generateKycLink() {
        if (this.isEmpty(this.subscriberId)) {
          this.showGenerateKycModal = false;
          Swal.fire("Failed!", "Missing subscriber reference for this merchant.", "warning");
          return;
        }
        this.isGenerating = true;
        // kycEload: true tags this as generated from the NEW "Generate KYC" card so the backend sets
        // ref_users.kyc_eload = 1 (the legacy KYC Details "Re-generate" flow does not send this).
        await this.generateKycUrl({ userId: this.subscriberId, kycEload: true })
          .then(async () => {
            await this.fetchKycDetails();
            this.generatedKycLink = this.generatedKycUrl || this.kycInfo.kycUrl || '';
            this.showGenerateKycModal = false;
            this.showGenerateKycSuccessModal = true;
          })
          .catch((err) => {
            console.error("generateKycLink error:", err);
            this.showGenerateKycModal = false;
            Swal.fire("Failed!", typeof err === 'string' ? err : "Unable to generate KYC link. Please try again.", "warning");
          })
          .finally(() => {
            this.isGenerating = false;
          });
      },
      async onCopyLinkClicked() {
        try {
          // Clicking copy on an expired link regenerates instead of copying stale data.
          if (this.kycInfo.isKycUrlExpired) {
            this.showGenerateKycSuccessModal = false;
            this.showGenerateKycModal = true;
            return;
          }
          await navigator.clipboard.writeText(this.generatedKycLink)
            .then(() => {
              // Keep the success modal open after copying; only the "Ok" button dismisses it.
              this.$toast.showToast('link', 'Copied Generated Link', 'Successfully copied the generated link');
            });
        } catch (error) {
          console.error("Error while copying link:", error);
        }
      },
      // React to a wallet freeze/enable performed in the sibling Wallet Balance card (profileWallet.vue)
      // so this card shows/hides live without a page reload. We only care about the eload wallet, since
      // the card is gated on the eload freeze state. Re-fetch the wallet status (source of truth); also
      // refresh KYC details so the resulting state (button vs copy) is accurate.
      async onWalletStatusChanged(payload) {
        if (!payload || payload.walletType !== 'eLoad') return;
        if (!this.isEmpty(payload.businessId) && String(payload.businessId) !== String(this.businessId)) return;
        await Promise.all([
          this.fetchEloadWalletStatus(),
          this.fetchKycDetails(),
        ]);
      },
    },
    async mounted() {
      this.businessId = this.$route.params.bid;
      // Listen for eload freeze/enable from the Wallet Balance card so this card toggles live.
      this.$root.$on('lite-wallet-status-changed', this.onWalletStatusChanged);
      // Both are independent reads; fetch them together. The card stays hidden until the eload wallet
      // is confirmed frozen (isEligible), so a failed/empty wallet fetch simply keeps the card hidden.
      await Promise.all([
        this.fetchKycDetails(),
        this.fetchEloadWalletStatus(),
      ]);
    },
    beforeDestroy() {
      // Avoid duplicate handlers/leaks when navigating between merchants.
      this.$root.$off('lite-wallet-status-changed', this.onWalletStatusChanged);
    }
  }
</script>

<template>
  <!-- Card renders only when the eload wallet is FROZEN and there is a KYC reason to act
       (no KYC, KYC age met, an active generated link to copy, or an expired link to re-generate). -->
  <div class="card" v-if="isEligible">
    <div class="card-header fw-bold generate-kyc-header">Generate KYC</div>
    <div class="card-body">
      <p class="font-size-13 text-muted mb-3">
        Share this secure link with the merchant to complete their identity verification.
      </p>

      <!-- State A: no active link (either first-time -> just the button, or an EXPIRED eload link ->
           show the stale link + "Generate New KYC Link" button + red expired note, per Figma). -->
      <div v-if="!hasActiveLink">
        <!-- Expired: keep the (stale) link visible above the action button, matching the design. -->
        <b-form-input
          v-if="isLinkExpired"
          disabled
          class="generate-kyc-link-input mb-2"
          v-model="generatedKycLink"
        ></b-form-input>
        <b-button
          type="button"
          class="btn generate-kyc-btn w-100 d-flex align-items-center justify-content-center"
          :disabled="isGenerating"
          @click="onGenerateClicked()"
        >
          <span v-if="isGenerating" class="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
          <i v-else-if="isLinkExpired" class="bx bx-link me-1"></i>
          {{ generateButtonLabel }}
        </b-button>
        <!-- Expired note (danger), replaces the "expires on ..." note once the link has lapsed. -->
        <p v-if="isLinkExpired" class="font-size-12 note-txt mt-2 mb-0">
          Your KYC link has expired. You can generate a new KYC link.
        </p>
      </div>

      <!-- State B: an eload/Peddlr Lite link exists (kyc_eload) -> show link + copy (Figma fig3). -->
      <div v-else>
        <b-form-input
          disabled
          class="generate-kyc-link-input mb-2"
          v-model="generatedKycLink"
        ></b-form-input>
        <b-button
          type="button"
          variant="primary"
          class="btn btn-primary w-100 d-flex align-items-center justify-content-center copy-link-btn"
          @click="onCopyLinkClicked()"
        >
          <i class="bx bx-link me-1"></i>Copy Link
        </b-button>
        <p class="font-size-12 text-muted mt-2 mb-0" v-if="linkExpiryNote()">{{ linkExpiryNote() }}</p>
      </div>
    </div>

    <!-- #region Confirm Generate KYC Modal -->
    <b-modal v-model="showGenerateKycModal" size="md" centered hide-header hide-footer hide-header-close no-close-on-backdrop>
      <div class="px-2 py-3">
        <div class="col-md-12 text-center">
          <i class="bx bx-user-pin mb-1"></i>
          <p class="fw-bold mt-2" style="font-size: 1.5em;">{{ confirmModalTitle }}</p>
          <p class="mt-2 mb-4">{{ confirmModalBody }}</p>
          <p class="small fst-italic note-txt">*Please note that the data will not be retrieved anymore once you confirm.</p>
          <div class="d-flex justify-content-center mt-4">
            <b-button type="button" variant="light" class="w-sm btn-cancel" @click="showGenerateKycModal = false">Cancel</b-button>
            <b-button type="button" class="btn w-sm ms-2 btn-ok" :disabled="isGenerating" @click="generateKycLink()">
              <span v-if="isGenerating" class="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
              Confirm
            </b-button>
          </div>
        </div>
      </div>
    </b-modal>
    <!-- #endregion -->

    <!-- #region Generate KYC Successful Modal -->
    <b-modal v-model="showGenerateKycSuccessModal" size="md" centered hide-header hide-footer hide-header-close no-close-on-backdrop>
      <div class="success-kyc-div">
        <div class="col-md-12 text-center">
          <img src="@/assets/images/check-circle.png" alt="Check Circle Icon" class="mb-1 check-icon">
          <p class="fw-bold mt-2" style="font-size: 1.5em;">{{ successModalTitle }}</p>
          <p class="mt-2 mb-4">{{ successModalBody }}</p>
          <div class="d-flex">
            <b-form-input disabled class="rounded-0 rounded-start" v-model="generatedKycLink"></b-form-input>
            <b-button
              type="button"
              variant="primary"
              class="btn rounded-0 rounded-end d-flex copy-link-btn text-center"
              @click="onCopyLinkClicked()"
            >
              <i class="bx bx-link"></i>Copy Link
            </b-button>
          </div>
          <div class="d-flex justify-content-center mt-4">
            <b-button type="button" variant="light" class="w-sm btn-cancel" @click="showGenerateKycSuccessModal = false">Ok</b-button>
          </div>
        </div>
      </div>
    </b-modal>
    <!-- #endregion -->
  </div>
</template>

<style scoped>
  .generate-kyc-header {
    background: transparent;
    border-bottom: 0;
    padding-bottom: 0;
  }

  /* Card primary action button ("Generate KYC" / "Generate New KYC Link") — violet, per the card design. */
  .generate-kyc-btn,
  .generate-kyc-btn:hover,
  .generate-kyc-btn:focus {
    background-color: #804997;
    border-color: #804997;
    color: #fff;
  }

  .generate-kyc-btn:disabled {
    background-color: #804997;
    border-color: #804997;
    opacity: 0.65;
  }

  /* Confirm modal's Confirm button — RED, matching the KYC Details modal and Figma (node 20212). */
  .btn-ok,
  .btn-ok:hover {
    background-color: #9A211F;
    border-color: #9A211F;
    color: #fff;
  }

  /* Confirm modal icon — red, same as KYC Details. */
  .bx-user-pin {
    font-size: 90px;
    color: #9A211F;
  }

  .note-txt {
    color: #9A211F;
    font-size: smaller;
  }

  .btn-cancel {
    border: 2px solid #B6B2EC;
  }

  .success-kyc-div {
    padding: 1rem 3.5rem;
  }

  .check-icon {
    width: 90px;
    height: 90px;
  }

  .bx-link {
    font-size: 20px;
  }

  /* Full-width Copy Link button in the card (Figma fig3). The success modal keeps its own
     side-by-side sizing via the .rounded-end button below. */
  .copy-link-btn {
    padding-left: 5px;
    padding-right: 5px;
  }

  /* Side-by-side Copy Link button inside the success modal keeps a fixed width. */
  .copy-link-btn.rounded-end {
    width: 142px;
  }

  /* Light, rounded read-only link field matching the Figma fig3 "link generated" state. */
  .generate-kyc-link-input {
    background-color: #f7f7f9;
    border-color: #e6e6ef;
    color: #6c757d;
    font-size: 13px;
  }
</style>
